import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  useTheme,
  Theme, // Ensure Theme is imported
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useAppLanguage } from '../hooks/useAppLanguage'; // NEW: Import the language hook

// Import chosen icons
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import DevicesIcon from '@mui/icons-material/Devices';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

// Define the structure for each feature
interface FeatureItem {
  icon: React.ElementType;
  title: string;
  description: React.ReactNode;
  iconColor?: string;
}

// --- Base Feature Content (non-translatable parts or keys for translation) ---
const baseFeatures = [
  {
    icon: WorkspacePremiumIcon,
    key: 'experienced', // Key for translation
    iconColor: 'primary.main',
  },
  {
    icon: PriceCheckIcon,
    key: 'accessible',
    iconColor: 'primary.main',
  },
  {
    icon: DevicesIcon,
    key: 'advanced',
    iconColor: 'primary.main',
  },
  {
    icon: EventAvailableIcon,
    key: 'scheduling',
    iconColor: 'primary.main',
  },
];

// --- Animation Variants ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

const WhyUs: React.FC = () => {
  const theme: Theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useAppLanguage(); // NEW: Initialize the language hook

  // NEW: Construct translated features within the component
  const translatedFeatures: FeatureItem[] = baseFeatures.map(feature => {
    const featureKeyPrefix = `whyUs.features.${feature.key}`;
    // Format the date according to the current language from i18next
    const currentMonthYear = new Date().toLocaleDateString(i18n.language, {
      month: 'long',
      year: 'numeric',
    });

    // Helper function to construct description JSX
    const createDescription = (): React.ReactNode => {
      switch (feature.key) {
        case 'experienced':
          return (
            <>
              {t(`${featureKeyPrefix}.descPart1`, "Our dedicated dentists bring over ")}
              <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                {t(`${featureKeyPrefix}.descBold1`, "a decade of clinical expertise")}
              </Box>
              {t(`${featureKeyPrefix}.descPart2`, ", prioritizing the highest quality care for your smile.")}
            </>
          );
        case 'accessible':
          return (
            <>
              {t(`${featureKeyPrefix}.descPart1`, "We welcome ")}
              <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                {t(`${featureKeyPrefix}.descBold1`, "all PPO insurance plans")}
              </Box>
              {t(`${featureKeyPrefix}.descPart2`, " and we are commited to providing ")}
              <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                {t(`${featureKeyPrefix}.descBold2`, "transparent and fair")}
              </Box>
              {t(`${featureKeyPrefix}.descPart3`, " pricing.")}
            </>
          );
        case 'advanced':
          return (
            <>
              {t(`${featureKeyPrefix}.descPart1`, "Our modern office features ")}
              <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                {t(`${featureKeyPrefix}.descBold1`, "state-of-the-art digital imaging and diagnostics")}
              </Box>
              {t(`${featureKeyPrefix}.descPart2`, ", paired with ")}
              <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                {t(`${featureKeyPrefix}.descBold2`, "premium A-dec chairs")}
              </Box>
              {t(`${featureKeyPrefix}.descPart3`, " for your utmost comfort.")}
            </>
          );
        case 'scheduling':
          return (
            <>
              {t(`${featureKeyPrefix}.descPart1`, "We understand busy lives. That's why we offer ")}
              <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                {t(`${featureKeyPrefix}.descBold1`, "flexible hours")}
              </Box>
              {t(`${featureKeyPrefix}.descPart2`, ", including ")}
              <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                {t(`${featureKeyPrefix}.descBold2`, "Saturday appointments")}
              </Box>
              {/* Using a specific key for the date part to allow interpolation of the formatted date string */}
              {t(`${featureKeyPrefix}.descPart3WithDate`, ` (as of ${currentMonthYear}), to accommodate your schedule.`, { date: currentMonthYear })}
            </>
          );
        default:
          // Fallback for a simple string description if a feature doesn't match the complex structure
          return t(`${featureKeyPrefix}.description`, "Default feature description.");
      }
    };

    return {
      icon: feature.icon,
      iconColor: feature.iconColor,
      title: t(`${featureKeyPrefix}.title`, `Default ${feature.key} Title`),
      description: createDescription(),
    };
  });

  return (
    <Box id="why-us" sx={{ py: { xs: 6, md: 10 }, bgcolor: 'grey.100' }}>
      <Container maxWidth="lg">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
         <Typography
            variant={isMobile ? "h4" : "h2"}
            component="h1"
            align="left"
            gutterBottom
            sx={{ 
              mb: { xs: 4, md: 6 },
              px: { xs: 0 },
              fontWeight: 700,
              color: 'text.secondary'
            }}
          >
            {/* NEW: Translate section title */}
            {t('whyUs.pageTitle', 'Why Choose Us?')}
          </Typography>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Grid container spacing={4} justifyContent="center">
            {/* NEW: Use translatedFeatures */}
            {translatedFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={6} key={index}>
                <motion.div variants={itemVariants} style={{ height: '100%' }}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: { xs: 2.5, md: 3.5 },
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      height: '100%',
                      borderRadius: 2,
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                         transform: 'translateY(-5px)',
                         boxShadow: theme.shadows[8],
                      }
                    }}
                  >
                    <Box sx={{ mr: 2.5, mt: 0.5 }}>
                      <feature.icon sx={{ fontSize: { xs: 38, md: 42 }, color: feature.iconColor || 'primary.main' }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: '600' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Box>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default WhyUs;
